import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Users,
  ShoppingCart,
  TrendingUp,
  Package,
  Tag,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  User,
  Calendar,
  LogOut,
} from "lucide-react";
import config from "../../config/config";
import { logout } from "../../redux/slices/adminSlice";
import { useDispatch } from "react-redux";
import SearchCard from "../../Components/Search/SearchCard";
import SearchCategoryCard from "../../Components/Search/SearchCategoryCard";

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [stats, setStats] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("keywords");
  const [tabSearchQuery, setTabSearchQuery] = useState("");

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login"); // Redirect if not authenticated
    } else {
      fetchAnalytics();
    }
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${config.backendUrl}/analytics/dashboardStats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStats(res.data.stats);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminInfo");
    navigate("/login");
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "search":
        return <Search className="w-5 h-5" />;
      case "category_click":
        return <Tag className="w-5 h-5" />;
      case "product_click":
        return <Package className="w-5 h-5" />;
      case "checkout_redirect":
        return <ExternalLink className="w-5 h-5" />;
      default:
        return <TrendingUp className="w-5 h-5" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "search":
        return "bg-blue-50 border-blue-200 text-blue-700";
      case "category_click":
        return "bg-purple-50 border-purple-200 text-purple-700";
      case "product_click":
        return "bg-orange-50 border-orange-200 text-orange-700";
      case "checkout_redirect":
        return "bg-green-50 border-green-200 text-green-700";
      default:
        return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case "search":
        return "bg-blue-100 text-blue-800";
      case "category_click":
        return "bg-purple-100 text-purple-800";
      case "product_click":
        return "bg-orange-100 text-orange-800";
      case "checkout_redirect":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "search":
        return "Search";
      case "category_click":
        return "Category View";
      case "product_click":
        return "Product View";
      case "checkout_redirect":
        return "Checkout Redirect";
      default:
        return action;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const groupBySession = (journey) => {
    const sessions = {};
    journey.forEach((step) => {
      if (!sessions[step.session_id]) sessions[step.session_id] = [];
      sessions[step.session_id].push(step);
    });
    return sessions;
  };

  const getVisitorSummary = (visitorId, journey) => {
    const sessions = groupBySession(journey);
    const sessionCount = Object.keys(sessions).length;
    const actionCount = journey.length;
    const buyClicks = journey.filter(
      (step) => step.data.action === "checkout_redirect"
    ).length;
    const lastActivity =
      journey.length > 0 ? journey[journey.length - 1].timestamp : null;
    return { sessionCount, actionCount, buyClicks, lastActivity };
  };

  const filteredVisitors = stats
    ? Object.entries(stats.user_journeys)
        .filter(([visitorId]) =>
          visitorId.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort(([, journeyA], [, journeyB]) => {
          const lastA =
            journeyA.length > 0
              ? new Date(journeyA[journeyA.length - 1].timestamp).getTime()
              : 0;

          const lastB =
            journeyB.length > 0
              ? new Date(journeyB[journeyB.length - 1].timestamp).getTime()
              : 0;

          // Recent first
          return lastB - lastA;
        })
    : [];

  const renderTabContent = () => {
    if (!stats) return null;

    const ROW_HEIGHT = 56; // px (approx)
    const MAX_VISIBLE = 5;
    const CONTAINER_HEIGHT = ROW_HEIGHT * MAX_VISIBLE;

    if (activeTab === "keywords") {
      const keywordEntries = Object.entries(stats.all_keywords || {})
        .filter(([k]) => k.toLowerCase().includes(tabSearchQuery.toLowerCase()))
        .sort((a, b) => b[1] - a[1]);

      if (!keywordEntries.length) {
        return <p className="p-6 text-gray-500">No keywords data</p>;
      }

      return (
        <div
          className="overflow-y-auto"
          style={{ maxHeight: CONTAINER_HEIGHT }}
        >
          {keywordEntries.map(([keyword, count]) => (
            <div
              key={keyword}
              className="p-4 flex justify-between border-b border-gray-200 hover:bg-gray-50"
            >
              <span className="font-semibold">{keyword}</span>
              <span className="text-gray-600">{count} searches</span>
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === "brands") {
      const brandEntries = Object.entries(stats.all_visited_brands || {})
        .filter(([b]) => b.toLowerCase().includes(tabSearchQuery.toLowerCase()))
        .sort((a, b) => b[1] - a[1]);

      if (!brandEntries.length) {
        return <p className="p-6 text-gray-500">No brand data</p>;
      }

      return (
        <div
          className="overflow-y-auto"
          style={{ maxHeight: CONTAINER_HEIGHT }}
        >
          {brandEntries.map(([brand, count]) => (
            <div
              key={brand}
              className="p-4 flex justify-between border-b border-gray-200 hover:bg-gray-50"
            >
              <span className="font-semibold">{brand}</span>
              <span className="text-gray-600">{count} visits</span>
            </div>
          ))}
        </div>
      );
    }
  };

  if (!stats) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">Loading Dashboard...</div>
      </div>
    );
  }

  // Detail View - Single Visitor
  if (selectedVisitor) {
    const journey = stats.user_journeys[selectedVisitor];
    const sessions = groupBySession(journey);

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <button
          onClick={() => setSelectedVisitor(null)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 font-semibold transition"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Visitors List
        </button>

        <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    Visitor Journey
                  </h2>
                  <p className="text-sm text-white text-opacity-80 font-mono">
                    {selectedVisitor}
                  </p>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
                <span className="text-white font-semibold">
                  {Object.keys(sessions).length} Session
                  {Object.keys(sessions).length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Total Actions</p>
              <p className="text-2xl font-bold text-gray-900">
                {journey.length}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Sessions</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.keys(sessions).length}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Buy Clicks</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  journey.filter(
                    (step) => step.data.action === "checkout_redirect"
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {Object.entries(sessions).map(([sessionId, steps], sessionIndex) => (
            <div key={sessionIndex} className="mb-6 last:mb-0">
              <button
                onClick={() => toggleSession(sessionId)}
                className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition mb-4"
              >
                <div className="flex items-center">
                  <h4 className="text-lg font-semibold text-gray-900 mr-3">
                    Session {sessionIndex + 1}
                  </h4>
                  <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                    {steps.length} actions
                  </span>
                  <span className="ml-3 text-sm text-gray-500">
                    {formatDate(steps[0].timestamp)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-gray-500 font-mono mr-3">
                    {sessionId.substring(0, 18)}...
                  </span>
                  {expandedSessions[sessionId] === false ? (
                    <ChevronDown />
                  ) : (
                    <ChevronUp />
                  )}
                </div>
              </button>

              {expandedSessions[sessionId] !== false && (
                <div className="pl-4">
                  {steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex mb-6 last:mb-0">
                      <div className="flex flex-col items-center mr-4">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${getActionColor(
                            step.data.action
                          )} border-2`}
                        >
                          {getActionIcon(step.data.action)}
                        </div>
                        {stepIndex < steps.length - 1 && (
                          <div className="w-0.5 h-16 bg-gray-300 my-2"></div>
                        )}
                      </div>

                      <div
                        className={`flex-1 p-4 rounded-lg border-2 ${getActionColor(
                          step.data.action
                        )}`}
                      >
                        <div className="flex justify-between items-center mb-3">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getActionBadgeColor(
                              step.data.action
                            )}`}
                          >
                            {getActionLabel(step.data.action)}
                          </span>
                          <span className="text-sm text-gray-600">
                            {formatTimestamp(step.data.timestamp)}
                          </span>
                        </div>

                        <div className="mt-3">
                          {step.data.action === "search" && (
                            <>
                              <p>Search Query: "{step.data.data.keyword}"</p>
                              {step.data.data?.searchResults && (
                                <>
                                  <p style={{ margin: "10px 0px " }}>
                                    Search Results:
                                  </p>
                                  <SearchCard
                                    searchResults={
                                      step.data.data?.searchResults
                                    }
                                    searchQuery={null}
                                  />
                                </>
                              )}
                            </>
                          )}
                          {step.data.action === "category_click" && (
                            <>
                              <p>Category: {step.data.data.category}</p>
                              {step.data.data?.products && (
                                <>
                                  <p style={{ margin: "10px 0px " }}>
                                    Category Products:
                                  </p>
                                  <SearchCategoryCard
                                    categoryCollection={
                                      step.data.data?.products
                                    }
                                    search={null}
                                  />
                                </>
                              )}
                            </>
                          )}
                          {step.data.action === "product_click" && (
                            <p>
                              Product:{" "}
                              {step.data.data.product_title ||
                                step.data.data.product_id}
                              , Brand: {step.data.data.brand}
                            </p>
                          )}
                          {step.data.action === "checkout_redirect" && (
                            <a
                              href={step.data.data.redirect_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {step.data.data.redirect_url}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // List View - All Visitors
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Top bar with logout */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl font-bold text-gray-900">
          Analytics Dashboard
        </h1>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* Dashboard stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Unique Visitors */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center mb-4">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
              <Users className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold">Unique Visitors</h3>
          </div>
          <p className="text-5xl font-bold">{stats.total_unique_visitors}</p>
        </div>
        {/* Total Visits */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center mb-4">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
              <TrendingUp className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold">Total Visits</h3>
          </div>
          <p className="text-5xl font-bold">{stats.total_sessions}</p>
        </div>
        {/* Buy Clicks */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center mb-4">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
              <ShoppingCart className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold">Buy Clicks</h3>
          </div>
          <p className="text-5xl font-bold">{stats.total_buy_clicks}</p>
        </div>
      </div>

      {/* Tabs for Keywords and Brands */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 py-3 text-center font-semibold transition ${
              activeTab === "keywords"
                ? "border-b-2 border-indigo-600 text-indigo-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("keywords")}
          >
            All Searches
          </button>
          <button
            className={`flex-1 py-3 text-center font-semibold transition ${
              activeTab === "brands"
                ? "border-b-2 border-indigo-600 text-indigo-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("brands")}
          >
            All Brands
          </button>
        </div>
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${
                activeTab === "keywords" ? "keywords" : "brands"
              }...`}
              value={tabSearchQuery}
              onChange={(e) => setTabSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        {renderTabContent()}
      </div>

      {/* Visitors List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">All Visitors</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Visitor ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {filteredVisitors.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No visitors found</p>
            </div>
          ) : (
            filteredVisitors.map(([visitorId, journey]) => {
              const summary = getVisitorSummary(visitorId, journey);

              return (
                <div
                  key={visitorId}
                  onClick={() => setSelectedVisitor(visitorId)}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="bg-indigo-100 p-3 rounded-full mr-4">
                        <User className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-mono text-sm text-gray-900 font-semibold mb-1">
                          {visitorId}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {summary.lastActivity
                              ? formatDate(summary.lastActivity)
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 ml-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.sessionCount}
                        </p>
                        <p className="text-xs text-gray-600">Sessions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.actionCount}
                        </p>
                        <p className="text-xs text-gray-600">Actions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {summary.buyClicks}
                        </p>
                        <p className="text-xs text-gray-600">Buy Clicks</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
